import { useState } from 'react'

import { useAppDispatch } from 'src/store/hooks'
import { showAlert } from 'src/actions/alert.actions'
import { handlerError, handlerSuccess } from 'src/selectors/error.selector'
import { getInventoryExistence } from 'src/actions/warehouse.actions'
import ReservedInventoryService, {
  ReservedInventoryItem,
} from 'src/services/reservedInventory.service'
import { InventoryExportParams } from './useInventoryExport'

const GENERIC_ERROR = 'No se pudo completar la operación. Intenta de nuevo.'
const LIST_ERROR = 'No se pudo cargar el inventario reservado.'

type Submitting = 'release' | 'remove' | null

// El thunk vive en JS (untyped); se toma el mensaje del backend (WSException) en el borde.
const errorMessage = (error: any, fallback: string = GENERIC_ERROR): string =>
  error?.response?.data?.message || fallback

export const useReservedInventory = (
  warehouseId: number | null,
  params?: InventoryExportParams,
) => {
  const dispatch = useAppDispatch()

  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<ReservedInventoryItem[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const [submitting, setSubmitting] = useState<Submitting>(null)

  const closeModal = () => {
    if (submitting !== null) return
    setOpen(false)
  }

  const openModal = async () => {
    if (warehouseId == null) return
    setItems([])
    setOpen(true)
    setLoadingList(true)
    try {
      const data = await ReservedInventoryService.getReserved(warehouseId)
      setItems(data || [])
    } catch (error) {
      setOpen(false)
      dispatch(showAlert(handlerError(errorMessage(error, LIST_ERROR))))
    } finally {
      setLoadingList(false)
    }
  }

  const reloadStock = () => {
    if (warehouseId == null) return
    dispatch(getInventoryExistence(warehouseId, params as any))
  }

  const onSuccess = (summary: string) => {
    setSubmitting(null)
    dispatch(
      showAlert({
        ...handlerSuccess(summary),
        onConfirm: () => {
          setOpen(false)
          reloadStock()
        },
      }),
    )
  }

  const onRelease = async () => {
    if (warehouseId == null) return
    setSubmitting('release')
    try {
      const result = await ReservedInventoryService.release(warehouseId)
      onSuccess(
        `Se liberaron ${result.affectedProducts} productos (${result.totalQuantity} unidades).`,
      )
    } catch (error) {
      setSubmitting(null)
      dispatch(showAlert(handlerError(errorMessage(error))))
    }
  }

  const onRemove = async () => {
    if (warehouseId == null) return
    setSubmitting('remove')
    try {
      const result = await ReservedInventoryService.remove(warehouseId)
      onSuccess(`Se generó la merma ${result.number} por Q${result.amount}.`)
    } catch (error) {
      setSubmitting(null)
      dispatch(showAlert(handlerError(errorMessage(error))))
    }
  }

  return {
    open,
    items,
    loadingList,
    submitting,
    openModal,
    closeModal,
    onRelease,
    onRemove,
  }
}
